import React, { useState, useEffect, useContext } from "react";
import AddProduct from "../components/AddProduct";
import UpdateProduct from "../components/UpdateProduct";
import AuthContext from "../AuthContext";
import * as XLSX from "xlsx";

function Inventory() {
  const [showProductModal, setShowProductModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateProduct, setUpdateProduct] = useState({});
  const [allProducts, setAllProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("All Locations");
  const [locations, setLocations] = useState([]);
  const [updatePage, setUpdatePage] = useState(true);
  const [stores, setAllStores] = useState([]);
  const [sales, setSales] = useState([]);
  const [error, setError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [file, setFile] = useState(null);

  const authContext = useContext(AuthContext);

  useEffect(() => {
    fetchProductsData();
    fetchStoresData();
    fetchSalesData();
  }, [updatePage]);

  // Fetching Data of All Products
  const fetchProductsData = () => {
    fetch(`https://stock.krishtec.co.in/stock/get_products.php`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        console.log("Raw Products Response:", text);
        try {
          const data = JSON.parse(text);
          if (data.error) throw new Error(data.error);
          const products = Array.isArray(data) ? data : [];
          setAllProducts(products);
          setDisplayedProducts(products);
          // Extract unique locations
          const uniqueLocations = [...new Set(products.map((p) => p.location).filter(Boolean))];
          setLocations(uniqueLocations);
        } catch (e) {
          throw new Error("Failed to parse response: " + e.message);
        }
      })
      .catch((err) => {
        console.log("Fetch products error:", err);
        setAllProducts([]);
        setDisplayedProducts([]);
        setLocations([]);
        setError("Failed to load products: " + err.message);
        setShowErrorModal(true);
      });
  };

  // Fetching all stores data
  const fetchStoresData = () => {
    fetch(`https://stock.krishtec.co.in/stock/get_stores.php`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        console.log("Raw Stores Response:", text);
        try {
          const data = JSON.parse(text);
          if (data.error) throw new Error(data.error);
          setAllStores(Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : []);
        } catch (e) {
          throw new Error("Failed to parse response: " + e.message);
        }
      })
      .catch((err) => {
        console.log("Fetch stores error:", err);
        setAllStores([]);
        setError("Failed to load stores: " + err.message);
        setShowErrorModal(true);
      });
  };

  // Fetching all sales data for revenue
  const fetchSalesData = () => {
    fetch(`https://stock.krishtec.co.in/stock/get_sales.php`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        console.log("Raw Sales Response:", text);
        try {
          const data = JSON.parse(text);
          if (data.error) throw new Error(data.error);
          setSales(Array.isArray(data) ? data : []);
        } catch (e) {
          throw new Error("Failed to parse response: " + e.message);
        }
      })
      .catch((err) => {
        console.log("Fetch sales error:", err);
        setSales([]);
        setError("Failed to load sales: " + err.message);
        setShowErrorModal(true);
      });
  };

  // Handle file upload
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      setError("No file selected.");
      setShowErrorModal(true);
      return;
    }
    if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
      setError("Please upload a valid Excel file (.xlsx or .xls).");
      setShowErrorModal(true);
      return;
    }
    setFile(selectedFile);
    setError(null);
  };

  // Process and upload Excel data
  const uploadExcelData = () => {
    if (!file) {
      setError("Please select a file to upload.");
      setShowErrorModal(true);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });

        // Format data with defaults
        const productsToUpload = jsonData.map((row) => {
          const productName = row["product_name"] || row["Product Name"] || row["ProductName"] || "";
          const inStock = row["in_stock"] || row["In Stock"] || row["InStock"] || 0;
          const location = row["location"] || row["Location"] || row["product_location"] || "-";
          const purchaseCount = row["purchase_count"] || row["Purchase Count"] || row["PurchaseCount"] || 0;
          const saleCount = row["sale_count"] || row["Sale Count"] || row["SaleCount"] || 0;
          const pricePerUnit = row["price_per_unit"] || row["Price Per Unit"] || row["PricePerUnit"] || 0;

          return {
            product_name: String(productName).trim(),
            in_stock: isNaN(parseInt(inStock, 10)) ? 0 : parseInt(inStock, 10),
            location: String(location).trim() || "-",
            purchase_count: isNaN(parseInt(purchaseCount, 10)) ? 0 : parseInt(purchaseCount, 10),
            sale_count: isNaN(parseInt(saleCount, 10)) ? 0 : parseInt(saleCount, 10),
            price_per_unit: isNaN(parseFloat(pricePerUnit)) ? 0 : parseFloat(pricePerUnit),
          };
        });

        if (productsToUpload.length === 0) {
          setError("Excel file is empty.");
          setShowErrorModal(true);
          return;
        }

        // Send data to backend
        console.log("Uploading products:", productsToUpload);
        fetch("https://stock.krishtec.co.in/stock/upload_products.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ products: productsToUpload }),
        })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return response.json();
          })
          .then((data) => {
            if (data.error) {
              setError(data.error);
              setShowErrorModal(true);
            } else {
              setError(null);
              setFile(null);
              handlePageUpdate();
            }
          })
          .catch((err) => {
            console.error("Upload error:", err);
            setError("Failed to upload products: " + err.message);
            setShowErrorModal(true);
          });
      } catch (e) {
        setError("Error reading Excel file: " + e.message);
        setShowErrorModal(true);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Filter products by search term and location
  useEffect(() => {
    let filtered = allProducts;
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((product) =>
        product.product_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    // Apply location filter
    if (selectedLocation !== "All Locations") {
      filtered = filtered.filter((product) => product.location === selectedLocation);
    }
    setDisplayedProducts(filtered);
  }, [allProducts, searchTerm, selectedLocation]);

  // Handle Location Filter
  const handleLocationChange = (e) => {
    setSelectedLocation(e.target.value);
  };

  // Modal for Product ADD
  const addProductModalSetting = () => {
    setShowProductModal(!showProductModal);
  };

  // Modal for Product UPDATE
  const updateProductModalSetting = (selectedProductData) => {
    setUpdateProduct(selectedProductData);
    setShowUpdateModal(!showUpdateModal);
  };

  // Delete item
  const deleteItem = (id) => {
    fetch("https://stock.krishtec.co.in/stock/delete_product.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product_id: id }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (data.error) {
          setError(data.error);
          setShowErrorModal(true);
        } else {
          handlePageUpdate();
        }
      })
      .catch((err) => {
        console.error("Error deleting product:", err);
        setError("Failed to delete product: " + err.message);
        setShowErrorModal(true);
      });
  };

  // Handle Page Update
  const handlePageUpdate = () => {
    setUpdatePage(!updatePage);
  };

  // Handle Search Term
  const handleSearchTerm = (e) => {
    setSearchTerm(e.target.value);
  };

  // Calculate total revenue from sales
  const totalRevenue = sales
    .reduce((sum, sale) => sum + parseFloat(sale.total_sale_amount || 0), 0)
    .toFixed(2);

  // Close error modal
  const closeErrorModal = () => {
    setShowErrorModal(false);
    setError(null);
  };

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center p-6 bg-gray-50 min-h-screen">
      <div className="w-full max-w-7xl">
        {/* Heading */}
        <h1 className="text-3xl font-bold text-gray-900 mb-6 text-center">Inventory</h1>

        {/* Error Popup Modal */}
        {showErrorModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-yellow-500 text-2xl">⚠️</span>
                <h3 className="text-lg font-semibold text-gray-900">Warning</h3>
              </div>
              <p className="text-gray-700 mb-6">{error}</p>
              <div className="flex justify-end">
                <button
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md"
                  onClick={closeErrorModal}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Overall Inventory */}
        <div className="bg-white rounded-lg p-6 shadow-md mb-6">
          <span className="font-semibold text-lg text-gray-900">Overall Inventory</span>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="flex flex-col p-4 bg-gray-50 rounded-md space-y-3">
              <span className="font-semibold text-blue-600 text-base">Total Products</span>
              <span className="font-semibold text-gray-600 text-base">{allProducts.length}</span>
              <span className="font-thin text-gray-400 text-xs">Last 7 days</span>
            </div>
            <div className="flex flex-col p-4 bg-gray-50 rounded-md space-y-3">
              <span className="font-semibold text-yellow-600 text-base">Stores</span>
              <div className="flex gap-8">
                <div className="flex flex-col space-y-3">
                  <span className="font-semibold text-gray-600 text-base">{stores.length}</span>
                  <span className="font-thin text-gray-400 text-xs">Last 7 days</span>
                </div>
                <div className="flex flex-col space-y-3">
                  <span className="font-semibold text-gray-600 text-base">Rs.{totalRevenue}</span>
                  <span className="font-thin text-gray-400 text-xs">Revenue</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col p-4 bg-gray-50 rounded-md space-y-3">
              <span className="font-semibold text-purple-600 text-base">Top Selling</span>
              <div className="flex gap-8">
                <div className="flex flex-col space-y-3">
                  <span className="font-semibold text-gray-600 text-base">
                    {allProducts.filter((p) => p.sale_count > 0).length}
                  </span>
                  <span className="font-thin text-gray-400 text-xs">Last 7 days</span>
                </div>
                <div className="flex flex-col space-y-3">
                  <span className="font-semibold text-gray-600 text-base">
                    Rs.
                    {allProducts
                      .reduce((sum, p) => sum + p.sale_count * p.price_per_unit, 0)
                      .toFixed(2)}
                  </span>
                  <span className="font-thin text-gray-400 text-xs">Cost</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col p-4 bg-gray-50 rounded-md space-y-3">
              <span className="font-semibold text-red-600 text-base">Low Stocks</span>
              <div className="flex gap-8">
                <div className="flex flex-col space-y-3">
                  <span className="font-semibold text-gray-600 text-base">
                    {allProducts.filter((p) => p.in_stock > 0 && p.in_stock <= 5).length}
                  </span>
                  <span className="font-thin text-gray-400 text-xs">Low Stock</span>
                </div>
                <div className="flex flex-col space-y-3">
                  <span className="font-semibold text-gray-600 text-base">
                    {allProducts.filter((p) => p.in_stock === 0).length}
                  </span>
                  <span className="font-thin text-gray-400 text-xs">Not in Stock</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showProductModal && (
          <AddProduct
            addProductModalSetting={addProductModalSetting}
            handlePageUpdate={handlePageUpdate}
          />
        )}
        {showUpdateModal && (
          <UpdateProduct
            updateProductData={updateProduct}
            updateModalSetting={updateProductModalSetting}
            handlePageUpdate={handlePageUpdate}
          />
        )}

        {/* Products Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="flex flex-col sm:flex-row justify-between p-4 gap-3">
            <div className="flex flex-col sm:flex-row gap-3 items-center">
              <span className="font-bold text-lg text-gray-900">Products</span>
              <div className="flex items-center w-full sm:w-1/2">
                <input
                  className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={handleSearchTerm}
                />
              </div>
              <div className="flex items-center w-full sm:w-1/2">
                <select
                  className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={selectedLocation}
                  onChange={handleLocationChange}
                >
                  <option value="All Locations">All Locations</option>
                  {locations.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center">
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={handleFileChange}
                  className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100"
                />
              </div>
              <div className="flex gap-3">
                <button
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-3 rounded-md text-sm"
                  onClick={uploadExcelData}
                >
                  Upload Data
                </button>
                <button
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-3 rounded-md text-sm"
                  onClick={addProductModalSetting}
                >
                  Add Product
                </button>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Product Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Purchase Count
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Sale Count
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    In Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Price per Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    More
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {displayedProducts.map((element) => (
                  <tr key={element.product_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-gray-900">{element.product_name || "N/A"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      {element.purchase_count || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      {element.sale_count || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      {element.in_stock || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      Rs.{element.price_per_unit ? Number(element.price_per_unit).toFixed(2) : "0.00"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      {element.location || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                      <span
                        className="text-green-700 cursor-pointer mr-2"
                        onClick={() => updateProductModalSetting(element)}
                      >
                        Edit
                      </span>
                      <span
                        className="text-red-600 cursor-pointer"
                        onClick={() => deleteItem(element.product_id)}
                      >
                        Delete
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {displayedProducts.length === 0 && (
            <div className="text-center text-gray-500 py-6">No products found.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Inventory;